// OneSignal Service for Android App Integration
// Handles OneSignal player ID registration with backend

import env from '../config/env';
import { selectedCompanyHeader } from '../hooks/userCompanies.shared';

interface OneSignalPlayerIdData {
  playerId: string;
  deviceId?: string;
  userAgent?: string;
}

class OneSignalService {
  private playerId: string | null = null;
  private isRegistered: boolean = false;

  constructor() {
    this.init();
  }

  private init() {
    // Check if we're running in the Android WebView
    if (this.isAndroidWebView()) {
      console.log(
        '[OneSignal] Running in Android WebView - setting up OneSignal integration'
      );
      this.setupWebViewIntegration();
    } else {
      console.log(
        '[OneSignal] Not running in Android WebView - OneSignal not available'
      );
    }
  }

  // Check if we're running in the Android WebView
  private isAndroidWebView(): boolean {
    const userAgent = navigator.userAgent;
    return (
      userAgent.includes('RelayTowingDriver') || userAgent.includes('Android')
    );
  }

  // Setup WebView integration to get OneSignal player ID
  private setupWebViewIntegration() {
    // Set up the callback function that will be called by the Android app
    (window as any).onOneSignalPlayerIdReceived = (playerId: string | null) => {
      console.log('[OneSignal] Received player ID from Android:', playerId);

      if (playerId) {
        this.playerId = playerId;
        this.registerPlayerIdWithBackend();
      } else {
        console.warn('[OneSignal] No player ID received from Android app');
      }
    };

    // Request the player ID from the Android app
    this.requestPlayerIdFromAndroid();
  }

  // Request OneSignal player ID from Android app
  private requestPlayerIdFromAndroid() {
    try {
      console.log('[OneSignal] Requesting player ID from Android app');

      // Call the Android JavaScript interface
      if (
        (window as any).ReactNativeWebView &&
        (window as any).ReactNativeWebView.getOneSignalPlayerId
      ) {
        (window as any).ReactNativeWebView.getOneSignalPlayerId();
      } else {
        console.warn('[OneSignal] ReactNativeWebView interface not available');
      }
    } catch (error) {
      console.error(
        '[OneSignal] Failed to request player ID from Android:',
        error
      );
    }
  }

  // Register OneSignal player ID with backend
  private async registerPlayerIdWithBackend(): Promise<void> {
    if (!this.playerId) {
      console.warn('[OneSignal] No player ID available for registration');
      return;
    }

    if (this.isRegistered) {
      console.log('[OneSignal] Player ID already registered');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        console.warn(
          '[OneSignal] No auth token available - will retry after authentication'
        );
        return;
      }

      const playerIdData: OneSignalPlayerIdData = {
        playerId: this.playerId,
        deviceId: this.getDeviceId(),
        userAgent: navigator.userAgent,
      };

      console.log('[OneSignal] Registering player ID with backend:', {
        playerId: playerIdData.playerId,
        deviceId: playerIdData.deviceId,
        userAgent: playerIdData.userAgent,
      });

      const response = await fetch(
        `${env.apiUrl}/driver-mobile/onesignal/player-id`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...selectedCompanyHeader(),
          },
          body: JSON.stringify(playerIdData),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          '[OneSignal] Backend registration failed:',
          response.status,
          errorText
        );
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();
      console.log(
        '[OneSignal] Player ID registered with backend successfully:',
        result
      );

      this.isRegistered = true;
    } catch (error) {
      console.error(
        '[OneSignal] Failed to register player ID with backend:',
        error
      );
    }
  }

  // Get device ID (for Android, this would be the Android ID)
  private getDeviceId(): string {
    // In a WebView, we can't get the actual Android device ID
    // The Android app should provide this via the JavaScript interface
    // For now, we'll use a fallback
    return 'webview-device-id';
  }

  // Public method to manually trigger registration (useful after authentication)
  async registerAfterAuthentication(): Promise<void> {
    if (this.playerId && !this.isRegistered) {
      console.log(
        '[OneSignal] Authentication completed - registering player ID'
      );
      await this.registerPlayerIdWithBackend();
    } else if (!this.playerId) {
      console.log(
        '[OneSignal] Authentication completed - requesting player ID from Android'
      );
      this.requestPlayerIdFromAndroid();
    }
  }

  /**
   * Registers the player id again, under the newly selected company. The
   * backend ties it to the Driver row in the company on the request, and
   * isRegistered kept a company switch from sending it again.
   */
  async registerForSelectedCompany(): Promise<void> {
    if (!this.playerId) return;
    this.isRegistered = false;
    await this.registerPlayerIdWithBackend();
  }

  // Unregister OneSignal player ID
  async unregisterPlayerId(): Promise<void> {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        console.warn('[OneSignal] No auth token available for unregistration');
        return;
      }

      const response = await fetch(
        `${env.apiUrl}/driver-mobile/onesignal/player-id`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            ...selectedCompanyHeader(),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('[OneSignal] Player ID unregistered successfully');
      this.isRegistered = false;
      this.playerId = null;
    } catch (error) {
      console.error('[OneSignal] Failed to unregister player ID:', error);
    }
  }

  // Send test notification
  async sendTestNotification(): Promise<void> {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        console.warn(
          '[OneSignal] No auth token available for test notification'
        );
        return;
      }

      const response = await fetch(
        `${env.apiUrl}/driver-mobile/onesignal/test`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            ...selectedCompanyHeader(),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('[OneSignal] Test notification sent:', result);
    } catch (error) {
      console.error('[OneSignal] Failed to send test notification:', error);
    }
  }

  // Get current registration status
  getRegistrationStatus(): { isRegistered: boolean; playerId: string | null } {
    return {
      isRegistered: this.isRegistered,
      playerId: this.playerId,
    };
  }
}

// Export singleton instance
export const oneSignalService = new OneSignalService();
export default oneSignalService;
